// Fictional preview content. Every name, number, and patient detail is invented.
// Shown when the real Supabase data is empty so the UI never looks lifeless.

// ── Top row: integrations + live data (combined) ────────────────────────
export type LiveTile = {
  key: string;
  brand: string;
  brandClass?: string;
  eyebrow: string;
  primary: string;
  sub: string;
  bullets?: { label: string; value: string }[];
  link?: string;
  status?: "ok" | "pending" | "muted" | "error";
};

export const LIVE: LiveTile[] = [
  { key: "xero", brand: "X", brandClass: "xero", eyebrow: "Xero · this month",
    primary: "$284k", sub: "+18% MoM · 14 unpaid · 2 overdue",
    bullets: [{ label: "Paid", value: "$241k" }, { label: "Overdue", value: "$8.4k" }],
    link: "https://go.xero.com/", status: "ok" },
  { key: "halaxy", brand: "H", brandClass: "halaxy", eyebrow: "Halaxy · today",
    primary: "6 patients", sub: "2 telehealth · 4 in-clinic · next 10:30",
    bullets: [{ label: "Active", value: "247" }, { label: "No-show", value: "1.8%" }],
    link: "https://halaxy.com", status: "ok" },
  { key: "whatsapp", brand: "W", brandClass: "wa", eyebrow: "WhatsApp · 3 threads",
    primary: "Anna · 11m", sub: "\"Terumo sheet ready — when can you look?\"",
    bullets: [{ label: "Unread", value: "5" }, { label: "Pinned", value: "Michelle" }],
    status: "ok" },
  { key: "coviu", brand: "C", brandClass: "coviu", eyebrow: "Coviu · telehealth",
    primary: "2 sessions", sub: "Next live at 10:30 · room ready",
    bullets: [{ label: "Quality", value: "HD" }, { label: "Avg", value: "42m" }],
    link: "https://coviu.com", status: "ok" },
  { key: "outlook", brand: "O", brandClass: "outlook", eyebrow: "Outlook · paused",
    primary: "47 unread", sub: "Awaiting Monique greenlight · Alfred off",
    bullets: [{ label: "Today", value: "12" }, { label: "Flag", value: "7 red" }],
    status: "muted" },
  { key: "ahpra", brand: "A", brandClass: "ahpra", eyebrow: "AHPRA · registration",
    primary: "Current", sub: "Renewal opens 1 Jun · clean history",
    bullets: [{ label: "CPD", value: "32/50" }, { label: "Years", value: "14" }],
    link: "https://www.ahpra.gov.au", status: "pending" },
  { key: "drive", brand: "D", brandClass: "drive", eyebrow: "Drive · sync",
    primary: "Synced", sub: "Last push 5m ago · 18 files this week",
    bullets: [{ label: "Used", value: "127 GB" }, { label: "Shared", value: "9" }],
    link: "https://drive.google.com", status: "ok" },
  { key: "imessage", brand: "S", brandClass: "sms", eyebrow: "iMessage · Alfred",
    primary: "Idle", sub: "0 queued · last brief held for review",
    bullets: [{ label: "Held", value: "1" }, { label: "Sent", value: "0" }],
    status: "pending" },
];

// ── KPI strip (CEO snapshot) ────────────────────────────────────────────
export const KPIS = [
  { label: "MTD revenue",    value: "$284", unit: "k",     trend: "+18%", dir: "up",   spark: "up"   },
  { label: "Patients today", value: "6",    unit: "appts", trend: "+1",   dir: "up",   spark: "up"   },
  { label: "Capacity",       value: "78",   unit: "%",     trend: "+6pt", dir: "up",   spark: "up"   },
  { label: "Inbox unread",   value: "47",   unit: "msgs",  trend: "-12%", dir: "down", spark: "down" },
  { label: "Awaiting reply", value: "8",    unit: "open",  trend: "-3",   dir: "up",   spark: "up"   },
  { label: "Outstanding",    value: "$42",  unit: "k",     trend: "-9%",  dir: "up",   spark: "up"   },
  { label: "NPS",            value: "72",   unit: "",      trend: "+4",   dir: "up",   spark: "up"   },
  { label: "New leads / wk", value: "14",   unit: "",      trend: "+3",   dir: "up",   spark: "up"   },
];

// ── Day at a glance ─────────────────────────────────────────────────────
export const GLANCE = {
  lead: {
    eyebrow: "Day at a glance",
    title: "Three patients, one vendor, one sign-off.",
    sub: "Clinic day · Sydney · clear and mild · 19°. Anna in clinic; Cathy off; Michelle remote until 14:00.",
  },
  morning: [
    { time: "08:30", title: "Huddle with Anna" },
    { time: "10:30", title: "Patient — telehealth intake", now: true },
    { time: "11:30", title: "Notes + Halaxy review" },
  ],
  midday: [
    { time: "12:00", title: "Lunch (blocked)" },
    { time: "13:00", title: "Patient — follow-up review" },
    { time: "14:00", title: "Terumo Q3 volumes call" },
  ],
  evening: [
    { time: "15:30", title: "Patient — IV therapy" },
    { time: "17:30", title: "May invoice run sign-off" },
    { time: "19:00", title: "Family dinner" },
  ],
};

// ── Hero "Today's three" ────────────────────────────────────────────────
export type HeroItem = {
  time: string;
  title: string;
  meta: string;
  chips: { text: string; kind?: "urgent" | "ok" | "warn" | "ghost" }[];
  urgent?: boolean;
};

// ── DECISION QUEUE — the things only Dom can decide today ──────────────
export type Decision = {
  id: string;
  title: string;
  context: string;
  source: string;
  due: string;
  urgency: "now" | "today" | "this-week";
  actions: { label: string; kind: "primary" | "edit" | "defer" }[];
};

export const DECISIONS: Decision[] = [
  {
    id: "d1",
    title: "Sign May invoice run",
    context: "47 invoices, $284k. Two flagged (one credit memo, one Express Path discrepancy).",
    source: "Michelle · Xero",
    due: "Today, EOD",
    urgency: "today",
    actions: [
      { label: "Approve all", kind: "primary" },
      { label: "Review flagged", kind: "edit" },
      { label: "Defer 24h", kind: "defer" },
    ],
  },
  {
    id: "d2",
    title: "Approve Anna's Terumo reply",
    context: "Drafted: yes on Q3 volumes pending pricing confirm. 2 paragraphs, your tone, signed DS.",
    source: "Anna · Email draft",
    due: "Before 14:00 call",
    urgency: "now",
    actions: [
      { label: "Approve & send", kind: "primary" },
      { label: "Read-back first", kind: "edit" },
      { label: "I'll write it", kind: "defer" },
    ],
  },
  {
    id: "d3",
    title: "Confirm dinner Saturday",
    context: "Anniversary booking · partner asked yesterday · 7pm Aria or Bennelong.",
    source: "iMessage · partner",
    due: "By Wed",
    urgency: "this-week",
    actions: [
      { label: "Yes — Aria", kind: "primary" },
      { label: "Yes — Bennelong", kind: "edit" },
      { label: "Suggest other", kind: "defer" },
    ],
  },
];

// ── NEXT UP — the very next thing on the calendar, prominent ────────────
export const NEXT_UP = {
  inMinutes: 24,
  startTime: "10:30",
  duration: "60 min",
  kind: "Telehealth · Coviu",
  title: "Patient — initial intake",
  brief: "New patient, presents with weight management + perimenopausal symptoms. Halaxy note primed. Coviu room ready.",
  prepActions: [
    { label: "Open Coviu", kind: "primary" },
    { label: "Review history", kind: "edit" },
    { label: "Brief me (60s)", kind: "edit" },
  ],
};

// ── TODAY'S PATIENTS (6 names, de-identified) ───────────────────────────
export const PATIENTS_TODAY = [
  { time: "10:30", initials: "K.L.", note: "New intake · weight + perimeno", mode: "Telehealth", chip: "now" as "now" | "soon" | "later" | "done" },
  { time: "13:00", initials: "R.T.", note: "Follow-up · IV protocol day 3",   mode: "In-clinic · rm 2", chip: "soon" },
  { time: "15:30", initials: "M.D.", note: "IV therapy · routine",             mode: "In-clinic · rm 1", chip: "later" },
  { time: "09:00", initials: "J.S.", note: "Bloods review (Express Path)",     mode: "Notes only", chip: "done" },
  { time: "11:30", initials: "P.O.", note: "Script renewal · phone consult",  mode: "Phone", chip: "later" },
  { time: "16:30", initials: "T.H.", note: "Initial consult — referral Dr Chen", mode: "In-clinic", chip: "later" },
];

export const HERO: HeroItem[] = [
  { time: "10:30 · TELEHEALTH", title: "Patient — initial intake",
    meta: "New patient, Coviu primed. Hx: weight mgmt + perimenopausal.",
    chips: [{ text: "Coviu ready", kind: "ok" }, { text: "Halaxy synced", kind: "ok" }] },
  { time: "14:00 · VENDOR", title: "Terumo Q3 volumes",
    meta: "Lock biologics tiers before quarter end. Pricing query first.",
    chips: [{ text: "Anna prepping", kind: "ok" }, { text: "$ pending", kind: "warn" }] },
  { time: "17:30 · APPROVAL", title: "May invoice run",
    meta: "47 invoices, $284k. Two flagged for review (credit + Express Path).",
    chips: [{ text: "Review needed", kind: "urgent" }, { text: "2 flagged", kind: "warn" }], urgent: true },
];

// ── Reminders / todos (tickable) ────────────────────────────────────────
export type Todo = { id: string; text: string; due?: string; source?: string; done?: boolean };

export const TODOS: Todo[] = [
  { id: "t1", text: "Sign May invoice run (credit memo flagged)", due: "Today, EOD", source: "Xero" },
  { id: "t2", text: "Confirm Q3 Terumo volumes — pricing first",  due: "By Fri",      source: "Email · Anna" },
  { id: "t3", text: "Review Express Pathology referral template", due: "This week",   source: "Drafts" },
  { id: "t4", text: "Sign off Layla's CPD allocation FY26",       due: "Next week",   source: "Slack",     done: true },
  { id: "t5", text: "Renew AHPRA registration",                   due: "1 Jun",       source: "AHPRA" },
  { id: "t6", text: "Coffee w/ Dr Chen (UWS partnership)",        due: "Thu 2pm",     source: "Calendar" },
];

// ── Apple Notes (read-only mirror) ──────────────────────────────────────
export type AppleNote = { folder: string; title: string; preview: string; updated: string };

export const NOTES: AppleNote[] = [
  { folder: "Biogenix",   title: "Q3 strategy — biologics push",
    preview: "Three angles to discuss with Anna: (1) Terumo volume tiers (2) Express Pathology rebate (3) staffing...",
    updated: "Today 09:14" },
  { folder: "Clinic",     title: "Conference takeaways · NZ Anti-aging summit",
    preview: "Key contacts: Dr Lim (Auckland), Maya Singh (peptide protocols), conf paper on...",
    updated: "Yesterday" },
  { folder: "Personal",   title: "Books to read",
    preview: "Atul Gawande — Being Mortal · Daniel Pink — Drive · Range by David Epstein...",
    updated: "Mon 22:08" },
  { folder: "Biogenix",   title: "Anna — operations review notes",
    preview: "Roster gaps Thu/Fri pm. CPD coverage for Layla. Cathy's holiday cover...",
    updated: "Sun 18:42" },
];

// ── Apple Reminders (separate list) ─────────────────────────────────────
export type AppleReminder = { list: string; text: string; due?: string; done?: boolean };

export const REMINDERS: AppleReminder[] = [
  { list: "Biogenix",  text: "Sign off PD budget by end of week",          due: "Fri" },
  { list: "Biogenix",  text: "Ask Anna re: Q3 supplier tender",            due: "Wed" },
  { list: "Personal",  text: "Sophie school pickup — Thu 3pm only",        due: "Thu" },
  { list: "Personal",  text: "Renew car rego",                             due: "21 May" },
  { list: "Family",    text: "Anniversary dinner booking",                 due: "Sat",  done: true },
];

// ── Schedule (time-block view) ──────────────────────────────────────────
export type ScheduleKind = "clinical" | "patient" | "vendor" | "admin" | "personal";
export type ScheduleItem = {
  start: string; end: string;
  title: string; meta: string;
  kind: ScheduleKind; now?: boolean;
};

export const SCHEDULE: ScheduleItem[] = [
  { start: "08:30", end: "09:00", kind: "admin",    title: "Morning huddle · Anna",   meta: "Clinic floor" },
  { start: "09:15", end: "10:15", kind: "clinical", title: "Pre-clinic notes review", meta: "Halaxy · roster" },
  { start: "10:30", end: "11:30", kind: "patient",  title: "Patient — intake",        meta: "Telehealth · Coviu", now: true },
  { start: "12:00", end: "12:45", kind: "personal", title: "Lunch",                    meta: "Blocked" },
  { start: "13:00", end: "13:45", kind: "patient",  title: "Patient — follow-up",     meta: "In-clinic · rm 2" },
  { start: "14:00", end: "14:45", kind: "vendor",   title: "Terumo · Q3 volumes",     meta: "Zoom · K. Mizoguchi" },
  { start: "15:30", end: "16:00", kind: "patient",  title: "Patient — IV therapy",    meta: "In-clinic · rm 1" },
  { start: "16:15", end: "17:00", kind: "admin",    title: "Inbox + drafts review",   meta: "Alfred-assisted" },
  { start: "17:30", end: "17:45", kind: "admin",    title: "Invoice sign-off",        meta: "Quick call · Michelle" },
];

export const DAY_START_HOUR = 8;
export const DAY_END_HOUR = 19;

// ── Inbox triage ────────────────────────────────────────────────────────
export type TriageItem = { initials: string; name: string; sub: string; count: number; flavour?: "red" | "amber" | "normal" };

export const TRIAGE: TriageItem[] = [
  { initials: "AM", name: "Anna Maemaranon",  sub: "Ops · 3 awaiting decision",    count: 3, flavour: "red" },
  { initials: "MS", name: "Michelle Sekulic", sub: "Billing · invoice + credit",   count: 2, flavour: "amber" },
  { initials: "LK", name: "Layla Kaur",       sub: "Clinical · CPD enrolment",     count: 1, flavour: "normal" },
  { initials: "CL", name: "Cathy Le",         sub: "Patient liaison · referrals",  count: 2, flavour: "normal" },
  { initials: "TE", name: "Terumo (external)",sub: "Q3 volumes · 3 unread",        count: 3, flavour: "amber" },
];

// ── Patient pipeline ────────────────────────────────────────────────────
export type PipelineCard = { name: string; sub: string };

export const PIPELINE: { col: string; items: PipelineCard[] }[] = [
  { col: "New enquiry", items: [
    { name: "S. R.", sub: "Gettimely · weight mgmt · today 09:14" },
    { name: "A. T.", sub: "Form · perimenopause · today 06:02" },
    { name: "M. B.", sub: "Referral · Dr Chen · yesterday" },
  ]},
  { col: "Awaiting consult", items: [
    { name: "K. L.", sub: "Intake booked Thu 10:30" },
    { name: "J. P.", sub: "Pathology pending · Express" },
  ]},
  { col: "Follow-up due", items: [
    { name: "R. H.", sub: "3-mo check · overdue 4d" },
    { name: "E. N.", sub: "Bloods due · Halaxy auto-ping" },
  ]},
];

export const AWAITING = [
  { name: "Terumo · K. Mizoguchi", sub: "Q3 contract draft · 3 days silent" },
  { name: "Express Pathology",     sub: "Referral form template · 1 day"   },
  { name: "Dr Chen (UWS)",          sub: "Partnership time · 2 days"        },
  { name: "Altus Medical",          sub: "Equipment quote f/up · 5 days"    },
];

export const REGULATORY = [
  { source: "AHPRA",  date: "12 May", title: "Updated advertising guidelines — testimonials clause amended" },
  { source: "TGA",    date: "11 May", title: "Biologics labelling — therapeutic claims threshold raised" },
  { source: "AHPRA",  date: "08 May", title: "Annual notice — registration renewal opens 1 June" },
  { source: "RACGP",  date: "06 May", title: "MBS item 23 — telehealth eligibility revision" },
];

export const CHAT_HISTORY = [
  { day: "TODAY", items: [
    { snippet: "Good morning, Dr Schaffner. Three things today — 10:30 consult, Terumo 14:00, Michelle waiting on invoice run.", time: "06:00 AM", active: true },
    { snippet: "Drafted the Anna reply on Terumo volumes — in your drafts.", time: "11:42 AM" },
  ]},
  { day: "YESTERDAY", items: [
    { snippet: "Layla's CPD allocation is approved on the FY26 budget if you greenlight.", time: "Yesterday 16:08" },
    { snippet: "Two new patient enquiries overnight via Gettimely — both self-booked.", time: "Yesterday 08:30" },
  ]},
  { day: "EARLIER THIS WEEK", items: [
    { snippet: "May P&L preview from Xero — revenue +18% MoM, expenses flat.", time: "Mon 18:14" },
    { snippet: "Express Pathology asked for an updated referral template — drafted.", time: "Mon 09:51" },
    { snippet: "AHPRA renewal window opens 1 June — added to reminders.", time: "Sun 11:00" },
  ]},
];

export const MESSAGES_PREVIEW = [
  { role: "assistant" as const, content: "Good morning, Dr Schaffner. Three things today — the 10:30 telehealth intake, Terumo at 14:00, and Michelle is waiting on the May invoice run sign-off.", mins: 180 },
  { role: "user" as const, content: "what's the terumo call about", mins: 178 },
  { role: "assistant" as const, content: "Q3 supply scheduling — biologics tiers. Anna has the volumes sheet in the shared drive. I've consolidated the last three threads into one brief; one tap and I'll read it.", mins: 177 },
  { role: "user" as const, content: "draft a reply to anna — yes on volumes, confirm pricing first", mins: 120 },
  { role: "assistant" as const, content: "Drafted. Subject: 'Terumo Q3 volumes — yes pending price confirm.' Two paragraphs, your usual tone, signed DS. Want a read-back before I mark it ready?", mins: 118 },
];

// ── Investments (portfolio + news) ──────────────────────────────────────
export type Holding = { ticker: string; name: string; alloc: string; value: string; chgPct: string; dir: "up" | "down" | "flat" };

export const HOLDINGS: Holding[] = [
  { ticker: "CSL.AX",  name: "CSL Limited",       alloc: "ASX · health",     value: "$84.2k",  chgPct: "+1.4%",  dir: "up"   },
  { ticker: "REA.AX",  name: "REA Group",         alloc: "ASX · property",   value: "$31.8k",  chgPct: "+0.6%",  dir: "up"   },
  { ticker: "VAS.AX",  name: "Vanguard AUS",      alloc: "ETF · broad",      value: "$112k",   chgPct: "+0.3%",  dir: "up"   },
  { ticker: "VGS.AX",  name: "Vanguard Intl",     alloc: "ETF · global",     value: "$96.5k",  chgPct: "-0.2%",  dir: "down" },
  { ticker: "NVDA",    name: "NVIDIA Corp",       alloc: "US · semis",       value: "$18.4k",  chgPct: "+2.1%",  dir: "up"   },
  { ticker: "BTC",     name: "Bitcoin",            alloc: "Crypto",           value: "$24.0k",  chgPct: "-1.8%",  dir: "down" },
];

export const PORTFOLIO_SUMMARY = {
  total: "$367,890",
  changeAud: "+$3,142",
  changePct: "+0.86%",
  changeDir: "up" as "up" | "down",
  asOf: "as of 11:42 AM AEST",
};

export type NewsItem = { source: string; date: string; tag: "AU" | "GLOBAL" | "HEALTH" | "TECH" | "CRYPTO"; title: string };

export const INVEST_NEWS: NewsItem[] = [
  { source: "AFR",      date: "1h",  tag: "AU",     title: "ASX 200 up 0.4% as CSL leads health sector higher" },
  { source: "Bloomberg",date: "3h",  tag: "GLOBAL", title: "Fed minutes signal patience on cuts; bond yields ease" },
  { source: "FT",       date: "5h",  tag: "HEALTH", title: "Biologics spending hits record as supply tightens" },
  { source: "Reuters",  date: "1d",  tag: "TECH",   title: "Nvidia raises guidance again, sees AI capex through 2027" },
  { source: "CoinDesk", date: "1d",  tag: "CRYPTO", title: "Bitcoin pulls back from $98k as profit-taking accelerates" },
];

// ── Personal · family · wellbeing ───────────────────────────────────────
export type PersonalCard = { eyebrow: string; primary: string; sub: string };

export const PERSONAL: PersonalCard[] = [
  { eyebrow: "Family", primary: "Sophie · school pickup Thu 3pm", sub: "School run · 1 reminder · partner unavailable" },
  { eyebrow: "Health", primary: "9,200 steps · 7h 12m sleep",      sub: "Apple Health · last 24h · resting HR 58 bpm" },
  { eyebrow: "Travel", primary: "NZ summit · 18 Jul",              sub: "Auckland · flights booked · 2 nights" },
  { eyebrow: "House",  primary: "Garden quote outstanding",         sub: "Followed up Tuesday · no reply yet" },
];

// ── Peptide protocol (Dom's personal cycle) ─────────────────────────────
export type PeptideDose = {
  time: string;          // "07:00"
  peptide: string;       // "BPC-157"
  dose: string;          // "250 mcg"
  route: string;         // "SubQ · abdominal"
  done?: boolean;
};

export type PeptideCycle = {
  name: string;
  startedOn: string;
  totalDays: number;
  daysCompleted: number;
  notes: string;
};

export const PEPTIDE_CYCLE: PeptideCycle = {
  name: "Recovery + GI protocol",
  startedOn: "Mon 5 May",
  totalDays: 28,
  daysCompleted: 8,
  notes: "Stack: BPC-157 + TB-500 (healing) + Ipamorelin (sleep/HGH). Refrigerated, reconstituted last Mon.",
};

export const PEPTIDE_TODAY: PeptideDose[] = [
  { time: "07:00", peptide: "BPC-157",    dose: "250 mcg", route: "SubQ · abdominal",    done: true },
  { time: "07:00", peptide: "TB-500",      dose: "2.5 mg",  route: "SubQ · alt site",     done: true },
  { time: "13:00", peptide: "Ipamorelin",  dose: "200 mcg", route: "SubQ · fasted state", done: false },
  { time: "21:30", peptide: "BPC-157",    dose: "250 mcg", route: "SubQ · pre-sleep",    done: false },
  { time: "21:30", peptide: "Ipamorelin",  dose: "300 mcg", route: "SubQ · pre-sleep",    done: false },
];

export const PEPTIDE_WEEK = [
  { day: "Mon",  label: "M",   compliance: 100 },
  { day: "Tue",  label: "T",   compliance: 100 },
  { day: "Wed",  label: "W",   compliance: 80  },
  { day: "Thu",  label: "T",   compliance: 100 },
  { day: "Fri",  label: "F",   compliance: 100 },
  { day: "Sat",  label: "S",   compliance: 60  },
  { day: "Sun",  label: "S",   compliance: 100 },
];

// ── Operations / staff ──────────────────────────────────────────────────
export const STAFF_PULSE = [
  { name: "Anna",     role: "Ops",       status: "In clinic",   chip: "ok"   },
  { name: "Michelle", role: "Billing",   status: "Remote · pm", chip: "ok"   },
  { name: "Layla",    role: "Clinical",  status: "In clinic",   chip: "ok"   },
  { name: "Cathy",    role: "Liaison",   status: "PTO today",   chip: "ghost"},
];

// Old, kept for type compat
export type Integration = LiveTile;
export const INTEGRATIONS = LIVE;
